/**
 * A company's request for more, waiting on Home — the invitation card's twin.
 * (D2, 4 Oct 2026)
 *
 * "<Company> asks to see your CheckUp score" (or the tests it names), answered
 * in place: Review (what they would and would not see, then Share), Not now
 * (out of Home for the snooze window, still in the bell), Decline (terminal,
 * the company is told — so it asks first). Each answer is a server record.
 */
import { useTranslation } from '@tonnie/locale';
import type { MyPendingInvitation } from '@tonnie/sdk';
import { Button, Card, CardContent } from '@tonnie/ui';
import { useState } from 'react';

import { CompanyMark } from '@/features/platform/invitations/components/CompanyMark';

import { useShareRequestAnswer } from '../hooks/use-share-request-answer';
import { useShareRequestTitle } from '../hooks/use-share-request-title';
import { ShareRequestDeclineConfirm } from './ShareRequestDeclineConfirm';
import { ShareRequestDialog } from './ShareRequestDialog';

export function ShareRequestCard({
  invitation,
}: {
  invitation: MyPendingInvitation & { request: NonNullable<MyPendingInvitation['request']> };
}) {
  const { t } = useTranslation();
  const company = invitation.companyName;
  const title = useShareRequestTitle(invitation.request, company);
  const answer = useShareRequestAnswer(invitation.token, company);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);

  return (
    <Card
      className="border-primary/30 bg-linear-to-br from-primary/10 via-primary/5 to-transparent shadow-sm animate-in fade-in slide-in-from-top-2 duration-300 motion-reduce:animate-none"
      data-testid="share-request-card"
      data-invitation-id={invitation.id}
      data-asks={invitation.request.asks}
    >
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <CompanyMark name={company} logo={invitation.companyLogo} />
          <div className="flex-1 min-w-0 space-y-1">
            <p className="text-2xs font-semibold uppercase tracking-wider text-primary-accessible">
              {t('invitations.request.eyebrow')}
            </p>
            <h2 className="text-base font-semibold leading-snug text-foreground">{title}</h2>
            <p className="text-sm text-muted-foreground">{t('invitations.request.cardBody')}</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            onClick={() => setReviewOpen(true)}
            disabled={answer.busy}
            data-testid="share-request-card-review"
          >
            {t('invitations.card.review')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => answer.notNow()}
            disabled={answer.busy}
            data-testid="share-request-card-not-now"
          >
            {t('invitations.card.notNow')}
          </Button>
          {/* Terminal and the company is told: the quietest action, and it asks first. */}
          <Button
            type="button"
            variant="link"
            className="ml-auto text-muted-foreground"
            onClick={() => setDeclineOpen(true)}
            disabled={answer.busy}
            data-testid="share-request-card-decline"
          >
            {t('invitations.card.decline')}
          </Button>
        </div>
      </CardContent>

      <ShareRequestDialog invitation={invitation} open={reviewOpen} onOpenChange={setReviewOpen} />
      <ShareRequestDeclineConfirm
        invitation={invitation}
        open={declineOpen}
        onOpenChange={setDeclineOpen}
      />
    </Card>
  );
}
